import { ProductsService } from '@/services/products.service';
import {
  isInvalidSupportEmailDomainError,
  isInvalidSupportEmailDomainMessage,
} from '@/utils/supportEmailDomainError';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';

export const SUPPORT_EMAIL_OTP_LEN = 4;
const RESEND_COOLDOWN_SEC = 60;

const autoSentSessionKeys = new Set<string>();

export function clearSupportEmailAutoSendSession(
  sendSessionKey: string | number,
  email: string,
) {
  autoSentSessionKeys.delete(`${sendSessionKey}:${email.trim()}`);
}

type SupportEmailApiResponse = {
  success?: boolean;
  already_verified?: boolean;
  sent?: boolean;
  error_code?: string;
};

type SendEmailOptions = {
  send?: boolean;
  force?: boolean;
};

function normalizeEmail(value: string | null | undefined) {
  return (value || '').trim().toLowerCase();
}

function applyOtpFromInput(
  index: number,
  rawValue: string,
  prevCode: string,
): string {
  const prev = prevCode.replace(/\D/g, '').slice(0, SUPPORT_EMAIL_OTP_LEN);
  const digits = rawValue.replace(/\D/g, '');

  if (digits.length > 1) {
    return (prev.slice(0, index) + digits).slice(0, SUPPORT_EMAIL_OTP_LEN);
  }

  if (digits.length === 1) {
    return (prev.slice(0, index) + digits + prev.slice(index + 1)).slice(
      0,
      SUPPORT_EMAIL_OTP_LEN,
    );
  }

  return (prev.slice(0, index) + prev.slice(index + 1)).slice(
    0,
    SUPPORT_EMAIL_OTP_LEN,
  );
}

type VerifyState = {
  phase: 'idle' | 'awaiting_code';
  code: string;
  error: string | null;
};

const initialVerifyState: VerifyState = {
  phase: 'idle',
  code: '',
  error: null,
};

type VerifyAction =
  | { type: 'RESET' }
  | { type: 'CODE_SENT' }
  | { type: 'SET_CODE'; code: string }
  | { type: 'SET_ERROR'; error: string };

function verifyReducer(state: VerifyState, action: VerifyAction): VerifyState {
  switch (action.type) {
    case 'RESET':
      return initialVerifyState;
    case 'CODE_SENT':
      return { phase: 'awaiting_code', code: '', error: null };
    case 'SET_CODE':
      return { ...state, code: action.code, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.error, code: '' };
    default:
      return state;
  }
}

export interface UseSupportEmailVerificationOptions {
  email: string;
  productName?: string;
  verifiedEmail: string | null;
  onVerified: (email: string) => void;
  onInvalidDomain?: () => void;
  onVerificationComplete?: () => void;
  autoSendOnMount?: boolean;
  /** Evita envio duplicado ao remontar (ex.: React Strict Mode / abertura do modal). */
  sendSessionKey?: string | number;
  /** Quando false, ignora callbacks de verificação (ex.: modal fechado). */
  enabled?: boolean;
}

export function useSupportEmailVerification({
  email,
  productName,
  verifiedEmail,
  onVerified,
  onInvalidDomain,
  onVerificationComplete,
  autoSendOnMount = true,
  sendSessionKey,
  enabled = true,
}: UseSupportEmailVerificationOptions) {
  const { t } = useTranslation(['common', 'onboarding']);
  const { toast } = useToast();
  const [verify, dispatch] = useReducer(verifyReducer, initialVerifyState);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const sendIntentRef = useRef<'send' | 'resend'>('send');
  const enabledRef = useRef(enabled);

  enabledRef.current = enabled;

  useEffect(() => {
    return () => {
      enabledRef.current = false;
    };
  }, []);

  const emailSupport = email.trim();

  const emailMatchesVerified =
    !!normalizeEmail(emailSupport) &&
    normalizeEmail(verifiedEmail) === normalizeEmail(emailSupport);

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const id = window.setTimeout(() => {
      setResendCooldownSec((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldownSec]);

  const resolveSupportEmailErrorMessage = (
    errorCode: string | undefined,
    fallbackKey: string,
    legacyMessage?: string,
  ): string => {
    switch (errorCode) {
      case 'invalid_domain':
        return t('supportEmailInvalidDomain');
      case 'invalid_email':
        return t('supportEmailVerificationErrorInvalidEmail');
      case 'send_failed':
        return t('supportEmailVerificationErrorSending');
      case 'invalid_code':
        return t('supportEmailVerificationCodeExpired');
      case 'server_error':
        return t('supportEmailVerificationErrorServer');
      default:
        if (isInvalidSupportEmailDomainMessage(legacyMessage)) {
          return t('supportEmailInvalidDomain');
        }
        return t(fallbackKey);
    }
  };

  const handleApiError = (
    errorCode: string | undefined,
    fallbackKey: string,
    legacyMessage?: string,
  ) => {
    if (isInvalidSupportEmailDomainError(errorCode)) {
      onInvalidDomain?.();
      return;
    }

    if (!errorCode && isInvalidSupportEmailDomainMessage(legacyMessage)) {
      onInvalidDomain?.();
      return;
    }

    toast({
      variant: 'error',
      description: resolveSupportEmailErrorMessage(
        errorCode,
        fallbackKey,
        legacyMessage,
      ),
    });
  };

  const completeVerification = (verifiedEmailValue: string) => {
    if (!enabledRef.current) return;
    onVerified(verifiedEmailValue);
    onVerificationComplete?.();
  };

  const handleSendSuccess = (
    data: SupportEmailApiResponse | undefined,
    isResend: boolean,
  ) => {
    if (data?.success && data.sent) {
      dispatch({ type: 'CODE_SENT' });
      setResendCooldownSec(RESEND_COOLDOWN_SEC);
      toast({
        variant: 'success',
        description: isResend
          ? t('supportEmailVerificationCodeResent')
          : t('supportEmailVerificationCodeSent'),
      });
      return;
    }

    if (data?.success && data.already_verified && !data.sent) {
      if (isResend) {
        handleApiError(
          data.error_code,
          'supportEmailVerificationErrorResending',
        );
        return;
      }

      completeVerification(emailSupport);
      toast({
        variant: 'success',
        description: t('supportEmailVerificationAlreadyVerified'),
      });
      return;
    }

    handleApiError(
      data?.error_code,
      isResend
        ? 'supportEmailVerificationErrorResending'
        : 'supportEmailVerificationErrorValidating',
    );
  };

  const {
    mutate: mutateSendEmail,
    isPending: isSendingEmail,
  } = useMutation({
    mutationFn: async (options?: SendEmailOptions) => {
      const response = await ProductsService.validateSupportEmail(
        emailSupport,
        productName,
        {
          send: options?.send ?? true,
          force: options?.force ?? false,
        },
      );
      return response.data as SupportEmailApiResponse;
    },
    onSuccess: (data) => {
      handleSendSuccess(data, sendIntentRef.current === 'resend');
    },
    onError: (error: AxiosError<SupportEmailApiResponse>) => {
      const isResend = sendIntentRef.current === 'resend';
      handleApiError(
        error.response?.data?.error_code,
        isResend
          ? 'supportEmailVerificationErrorResending'
          : 'supportEmailVerificationErrorSending',
      );
    },
  });

  const {
    mutate: mutateVerifyCode,
    isPending: isVerifyingCode,
  } = useMutation({
    mutationFn: async (code: string) => {
      const response = await ProductsService.verifySupportEmailCode(
        emailSupport,
        code,
      );
      return response.data as SupportEmailApiResponse;
    },
    onSuccess: (data) => {
      if (data?.success) {
        completeVerification(emailSupport);
        toast({
          variant: 'success',
          description: t('supportEmailVerificationEmailVerified'),
        });
        return;
      }

      dispatch({
        type: 'SET_ERROR',
        error: resolveSupportEmailErrorMessage(
          data?.error_code,
          'supportEmailVerificationCodeExpired',
        ),
      });
    },
    onError: (error: AxiosError<SupportEmailApiResponse>) => {
      dispatch({
        type: 'SET_ERROR',
        error: resolveSupportEmailErrorMessage(
          error.response?.data?.error_code,
          'supportEmailVerificationCodeExpired',
        ),
      });
    },
  });

  const busy: null | 'send' | 'verify' | 'resend' = isVerifyingCode
    ? 'verify'
    : isSendingEmail
      ? sendIntentRef.current
      : null;

  const sendVerificationEmail = () => {
    if (!emailSupport || isSendingEmail) return;
    sendIntentRef.current = 'send';
    mutateSendEmail({ send: true, force: false });
  };

  useEffect(() => {
    if (!enabled) return;
    if (!autoSendOnMount) return;
    if (!emailSupport) return;
    if (emailMatchesVerified) return;
    if (verify.phase !== 'idle') return;

    const sessionKey =
      sendSessionKey != null
        ? `${sendSessionKey}:${emailSupport}`
        : emailSupport;

    if (autoSentSessionKeys.has(sessionKey)) return;

    autoSentSessionKeys.add(sessionKey);
    sendVerificationEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailSupport,
    emailMatchesVerified,
    autoSendOnMount,
    sendSessionKey,
    enabled,
    verify.phase,
  ]);

  useEffect(() => {
    if (sendSessionKey == null) return;

    return () => {
      dispatch({ type: 'RESET' });
    };
  }, [sendSessionKey]);

  useEffect(() => {
    if (verify.phase !== 'awaiting_code') return;
    if (verify.code !== '') return;
    otpInputRefs.current[0]?.focus();
  }, [verify.code, verify.phase]);

  useEffect(() => {
    if (!enabled) return;
    if (verify.phase !== 'awaiting_code') return;
    if (emailMatchesVerified) return;
    if (isVerifyingCode) return;

    const code = verify.code.replace(/\D/g, '');
    if (code.length !== SUPPORT_EMAIL_OTP_LEN) return;

    mutateVerifyCode(code);
  }, [
    enabled,
    emailMatchesVerified,
    isVerifyingCode,
    mutateVerifyCode,
    verify.code,
    verify.phase,
  ]);

  const otpDigits = verify.code
    .replace(/\D/g, '')
    .slice(0, SUPPORT_EMAIL_OTP_LEN);
  const otpDisabled =
    busy === 'verify' ||
    busy === 'send' ||
    busy === 'resend' ||
    emailMatchesVerified;

  const handleOtpChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;
    const entered = raw.replace(/\D/g, '');
    const prev = verify.code.replace(/\D/g, '').slice(0, SUPPORT_EMAIL_OTP_LEN);

    if (entered.length > 1) {
      const next = applyOtpFromInput(index, raw, verify.code);
      dispatch({ type: 'SET_CODE', code: next });
      const focusAt = Math.min(
        index + entered.length,
        SUPPORT_EMAIL_OTP_LEN - 1,
      );
      window.requestAnimationFrame(() => {
        otpInputRefs.current[focusAt]?.focus();
        otpInputRefs.current[focusAt]?.select();
      });
      return;
    }

    if (entered.length === 1 && index > prev.length) {
      const at = prev.length;
      const next = (prev.slice(0, at) + entered + prev.slice(at + 1)).slice(
        0,
        SUPPORT_EMAIL_OTP_LEN,
      );
      dispatch({ type: 'SET_CODE', code: next });
      const nextFocus = Math.min(at + 1, SUPPORT_EMAIL_OTP_LEN - 1);
      window.requestAnimationFrame(() =>
        otpInputRefs.current[nextFocus]?.focus(),
      );
      return;
    }

    const next = applyOtpFromInput(index, raw, verify.code);
    dispatch({ type: 'SET_CODE', code: next });
    if (entered.length === 1 && index < SUPPORT_EMAIL_OTP_LEN - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key !== 'Backspace') return;
    if (e.currentTarget.value !== '' || index === 0) return;
    e.preventDefault();
    const prev = verify.code.replace(/\D/g, '');
    dispatch({
      type: 'SET_CODE',
      code: prev.slice(0, index - 1),
    });
    window.requestAnimationFrame(() =>
      otpInputRefs.current[index - 1]?.focus(),
    );
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, SUPPORT_EMAIL_OTP_LEN);
    if (!pasted) return;
    dispatch({ type: 'SET_CODE', code: pasted });
    const focusAt = Math.min(pasted.length, SUPPORT_EMAIL_OTP_LEN - 1);
    window.requestAnimationFrame(() => {
      otpInputRefs.current[focusAt]?.focus();
      otpInputRefs.current[focusAt]?.select();
    });
  };

  const handleVerifyCode = () => {
    const code = verify.code.replace(/\D/g, '');

    if (!emailSupport || code.length !== SUPPORT_EMAIL_OTP_LEN) {
      dispatch({
        type: 'SET_ERROR',
        error: t('supportEmailVerificationInvalidCode'),
      });
      return;
    }

    if (isVerifyingCode) return;
    mutateVerifyCode(code);
  };

  const handleResendCode = () => {
    if (!emailSupport || isSendingEmail) return;
    sendIntentRef.current = 'resend';
    mutateSendEmail({ send: true, force: true });
  };

  return {
    emailSupport,
    emailMatchesVerified,
    verify: { ...verify, busy },
    codeError: verify.error,
    otpDigits,
    otpDisabled,
    otpInputRefs,
    resendCooldownSec,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerifyCode,
    handleResendCode,
    sendVerificationEmail,
  };
}
